import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { PRODUCT_ELIGIBILITY_STATUS_SELECTS } from 'src/app/core/constants/inventory-status.constant';
import { Logic } from 'src/app/core/infrastructure/enums/logic.enum';
import {
  CampaignProductAdAggregatedField,
  InventoryStatus,
  SuggestedAction,
} from 'src/app/core/infrastructure/enums/meta-data-field.enum';

const DEFAULT_INVENTORY_STATUS_FILTER: InventoryStatus[] = [
  InventoryStatus.HIGH_RISK,
  InventoryStatus.MODERATE_RISK,
];

function buildInventoryStatusFilter(): CompositeFilterDescriptor {
  return {
    logic: 'or',
    filters: DEFAULT_INVENTORY_STATUS_FILTER.map((status) => ({
      field: CampaignProductAdAggregatedField.inventoryStatus,
      operator: 'eq',
      value: status,
    })),
  };
}

export function buildActiveFilter(active = true): CompositeFilterDescriptor {
  return {
    logic: 'and',
    filters: [
      {
        field: CampaignProductAdAggregatedField.active,
        operator: 'eq',
        value: active,
      },
    ],
  };
}

export function buildPauseItemFilter(
  selectedValues: string[],
): CompositeFilterDescriptor {
  return {
    logic: 'or',
    filters: PRODUCT_ELIGIBILITY_STATUS_SELECTS.filter((item) =>
      selectedValues.includes(item.value),
    ).map((item) => ({
      field: CampaignProductAdAggregatedField.eligibilityStatus,
      operator: 'eq',
      value: item.value,
    })),
  };
}

export const DEFAULT_PRODUCT_AD_FILTERS: CompositeFilterDescriptor[] = [
  buildInventoryStatusFilter(),
  buildActiveFilter(),
];

export const DEFAULT_CAMPAIGN_FILTERS: CompositeFilterDescriptor[] = [
  buildInventoryStatusFilter(),
];

export function applyDefaultFiltersIfNeeded(
  state: State,
  defaultFilters: CompositeFilterDescriptor[] = DEFAULT_PRODUCT_AD_FILTERS,
): void {
  const hasFilters = !!state.filter?.filters?.length;

  if (hasFilters) return;

  state.filter = {
    logic: Logic.and,
    filters: [...defaultFilters],
  };
}

export function buildActionCenterFilter(riskId: string): CompositeFilterDescriptor {
  let inventoryStatus: InventoryStatus | null = null;
  let active = true;
  let suggestAction: SuggestedAction | null = null;

  switch (riskId) {
    case 'active-high':
    case 'active-high-managed':
    case 'active-high-external':
      inventoryStatus = InventoryStatus.HIGH_RISK;
      active = true;
      break;
    case 'active-mod':
    case 'active-mod-managed':
    case 'active-mod-external':
      inventoryStatus = InventoryStatus.MODERATE_RISK;
      active = true;
      break;
    case 'paused-mod':
    case 'paused-mod-managed':
    case 'paused-mod-external':
      inventoryStatus = InventoryStatus.MODERATE_RISK;
      active = false;
      break;
    case 'paused-well':
    case 'paused-well-managed':
    case 'paused-well-external':
      inventoryStatus = InventoryStatus.WELL_STOCK;
      active = false;
      suggestAction = SuggestedAction.ENABLE;
      break;
  }

  if (!inventoryStatus) return { logic: Logic.and, filters: [] };

  const baseFilters: CompositeFilterDescriptor['filters'] = [
    {
      logic: Logic.and,
      filters: [{ field: CampaignProductAdAggregatedField.inventoryStatus, operator: 'eq', value: inventoryStatus }]
    },
    {
      logic: Logic.and,
      filters: [{ field: CampaignProductAdAggregatedField.active, operator: 'eq', value: active }]
    },
    ...(suggestAction ? [{
      logic: Logic.and as const,
      filters: [{ field: CampaignProductAdAggregatedField.suggestAction, operator: 'eq', value: suggestAction }]
    }] : []),
    {
      logic: Logic.or,
      filters: [
        { field: CampaignProductAdAggregatedField.isAutomatic, operator: 'eq', value: false },
        { field: CampaignProductAdAggregatedField.isAutomatic, operator: 'isnull', value: null }
      ]
    }
  ];

  if (riskId === 'active-high-managed' || riskId === 'active-mod-managed' || riskId === 'paused-well-managed' || riskId === 'paused-mod-managed') {
    baseFilters.push({
      logic: Logic.and,
      filters: [{ field: CampaignProductAdAggregatedField.managedBySystem, operator: 'eq', value: true }]
    });
  } else if (riskId === 'paused-well-external' || riskId === 'paused-mod-external' || riskId === 'active-high-external' || riskId === 'active-mod-external') {
    baseFilters.push({
      logic: Logic.and,
      filters: [{ field: CampaignProductAdAggregatedField.managedBySystem, operator: 'eq', value: false }]
    });
  }

  return { logic: Logic.and, filters: baseFilters };
}
